/* ------------------------------------------------------------------ *
 *  Front Desk dashboard API client.
 *  Every call goes through the gateway (default http://localhost:54187 while debugging in Visual Studio).
 *  The JWT is read from localStorage unless a `getToken` function is passed to <Frontdesk />.
 * ------------------------------------------------------------------ */

export const DEFAULT_API_BASE = "http://localhost:54187";

export interface ApiOptions {
  /** Gateway base URL, no trailing slash. */
  apiBase?: string;
  /** Returns the JWT (or null when signed out). */
  getToken?: () => string | null | undefined | Promise<string | null | undefined>;
}

export interface PeriodPair<T> {
  month: T;
  year: T;
}

export interface DashboardKpi {
  inbound: number;
  outbound: number;
  answered: number;
  resolved: number;
}
export interface DashboardVolume {
  labels: string[];
  inbound: number[];
  outbound: number[];
  missed: number[];
}
export interface DashboardActivity {
  talkMinutes: number;
  calls: number;
  serviceLevelPercent: number;
}
export interface DashboardAgent {
  agentId: string;
  name: string;
  calls: number;
  talkSeconds: number;
  share: number;
}
export interface DashboardCallRow {
  id: string;
  direction: string;
  caller: string;
  line: string;
  startedAt: string;
  localTime: string;
  durationSeconds: number;
  status: string;
  agent: string | null;
}
export interface DashboardDaily {
  date: string;
  hours: number[];
  calls: number[];
  recent: DashboardCallRow[];
}
export interface DashboardData {
  asOf: string;
  timeZone: string;
  kpis: PeriodPair<DashboardKpi>;
  volume: PeriodPair<DashboardVolume>;
  activity: PeriodPair<DashboardActivity>;
  callsByHour: PeriodPair<number[]>;
  agentsOnline: number;
  agents: PeriodPair<DashboardAgent[]>;
  callLog: PeriodPair<DashboardCallRow[]>;
  daily: DashboardDaily;
}
export interface DashboardDepartment {
  queueId: string | null;
  name: string;
  calls: number;
  answered: number;
  missed: number;
  share: number;
}
export interface DashboardDepartments {
  from: string;
  to: string;
  departments: DashboardDepartment[];
}
export interface DashboardSlot {
  hour: number;
  calls: number;
  average: number;
}
export interface DashboardSlots {
  date: string;
  slots: DashboardSlot[];
}
export interface CallbackItem {
  id: string;
  number: string;
  status: string;
  requestedAt: string;
  scheduledFor: string | null;
  attemptCount: number;
  note: string | null;
}

const TOKEN_KEYS = ["access_token", "accessToken", "token", "jwt"];

const readStoredToken = (): string | null => {
  try {
    for (const key of TOKEN_KEYS) {
      const v = window.localStorage.getItem(key) ?? window.sessionStorage.getItem(key);
      if (v) return v.replace(/^"|"$/g, "");
    }
  } catch {
    /* storage blocked */
  }
  return null;
};

const timeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

async function get<T>(opts: ApiOptions, path: string, params: Record<string, string | number | undefined>, signal?: AbortSignal): Promise<T> {
  const base = (opts.apiBase ?? DEFAULT_API_BASE).replace(/\/+$/, "");
  const query = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") query.set(k, String(v));
  const token = (await opts.getToken?.()) ?? readStoredToken();

  const res = await fetch(`${base}${path}${query.toString() ? `?${query}` : ""}`, {
    signal,
    headers: { Accept: "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) {
    let detail = "";
    try {
      const body = await res.json();
      detail = body?.message ?? body?.title ?? "";
    } catch {
      /* not json */
    }
    throw new Error(res.status === 401 ? "Please sign in again." : res.status === 403 ? "You do not have access to the dashboard." : detail || `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

export const getDashboard = (opts: ApiOptions, signal?: AbortSignal) =>
  get<DashboardData>(opts, "/api/analytics/dashboard", { timezone: timeZone() }, signal);

export const getDepartments = (opts: ApiOptions, q: { year?: number; month?: number; day?: number }, signal?: AbortSignal) =>
  get<DashboardDepartments>(opts, "/api/analytics/dashboard/departments", { ...q, timezone: timeZone() }, signal);

export const getSlots = (opts: ApiOptions, date: string, signal?: AbortSignal) =>
  get<DashboardSlots>(opts, "/api/analytics/dashboard/slots", { date, timezone: timeZone() }, signal);

export const getPendingCallbacks = async (opts: ApiOptions, signal?: AbortSignal): Promise<CallbackItem[]> => {
  const page = await get<{ items: CallbackItem[] }>(opts, "/api/callbacks", { status: "pending", pageSize: 50 }, signal);
  return page.items ?? [];
};